import { BugoBrandBrief } from '@shared/api/bugoBrand/bugoBrand.interface';
import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Member } from '@shared/api/member/member.interface';
import { ShopItem } from '@shared/api/shopItem/shopItem.interface';
import { ShopOrder } from '@shared/api/shopOrder/shopOrder.interface';
import { QueryDataState } from '@shared/interfaces';
import { FeventDate, Gender } from '@shared/types';

import { Bugo } from '../bugo/bugo.interface';
import { FuneralHomeInfoEmbed } from '../funeralHomeInfoRequest/funeralHomeInfoRequest.interface';
import { User } from '../user/user.interface';
import { DeceasedReligion } from './deceasedReligion';
import { FeventCemetery } from './feventCemetery.util';

export enum FeventStatus {
  Todo = 'Todo',
  Doing = 'Doing',
  Done = 'Done',
}

//* 부고 추가 일정(영결식·입관예배 등) — 고정 3일정(입실/입관/발인) 중 after 가 가리키는 행 바로 뒤에 표시
//* 신규 API 없음, 기존 fevent 생성/수정 경로에 얹음. 규칙·프리셋은 feventExtraSchedule.util.ts (docs/fevent-extra-schedules-spec.md)
export type FeventExtraScheduleAfter = 'enterance' | 'coffinIn' | 'coffinOut';
export type FeventExtraSchedule = {
  _id?: string;
  label: string; // trim 1~20자
  date: Date; // axios 인터셉터가 ISO → Date 복원
  timeDefined: boolean; // false=날짜만 표시(고정 일정과 같은 의미)
  after: FeventExtraScheduleAfter;
};

export type Fevent<
  User = string,
  FuneralHomeInfo = string,
  Members = undefined,
  ShopOrders = undefined,
  MemberOrders = string[],
  DpImage = string,
  CashAccounts = undefined,
  CourtesyAsset = string,
  CourtesyContent = string,
  Bugos = undefined,
  BugoBrandRef = string,
> = {
  _id: string;
  user: User;
  bugoBrand: BugoBrandRef;
  bugoBrandAgencyIndex?: number;
  registerNumber?: string;
  funeralHomeInfo: FuneralHomeInfo;
  funeralHomeInfoEmbed?: FuneralHomeInfoEmbed;
  //* 호실은 직접 입력(embed)만. 호실 참조(roomInfo)는 실데이터 0건이라 프론트에서 제거 (docs/fevent-room-info-ref-deadpath.md)
  roomInfoEmbed?: {
    name: string;
  };
  meta: {
    deletedAt: Date;
    willBeDeletedAt: Date;
    isDeleted: boolean;
    courtesySend: boolean;
    willCourtesySend: Date;
    shareCount: number;
  };
  members: Members;
  shopOrders: ShopOrders;
  cashAccounts?: CashAccounts;
  memberOrderList: MemberOrders;
  mournerText: string;
  deceasedInfo: {
    age: number;
    name: string;
    shareDisplayName?: string;
    nameDetail?: string;
    cemetery: string;
    coffinIn: FeventDate;
    coffinOut: FeventDate;
    death: FeventDate;
    enterance: FeventDate;
    note?: string;
    religion?: DeceasedReligion;
    bugoTerm: string;
    sex: Gender;
    dpImage?: DpImage;
    extraSchedules?: FeventExtraSchedule[]; // 구문서엔 없음 → [] 취급
    //* 장지 1차·2차 [{label,name}] — 백엔드 스키마 요청(docs/fevent-cemeteries-spec.md). 없으면 cemetery 문자열 그대로 표시
    cemeteries?: FeventCemetery[];
  };
  courtesyAsset: CourtesyAsset;
  courtesyContent: CourtesyContent;
  status: FeventStatus;
  hardCustomConfig?: {
    /** 링크 미리보기 이미지. 미등록 시 사이트 기본 OG 이미지를 사용한다. */
    ogImage?: Image | string | null;
    useCustomConfig?: boolean;
    headerImage?: Image;
    footerImage?: Image;
    hideFlowerOrderLink?: boolean;
    emptyAccountMessage?: string;
    //* 조의금 사절: 부의금 섹션을 안내 문구로 대체(+SMS 기본 문구의 계좌 생략). 구서버 strict 스키마는 조용히 버림 — docs/fevent-hide-condolence-money-spec.md
    hideCondolenceMoney?: boolean;
    condolenceMoneyMessage?: string;
  };
  hiddenBugoShopOrder?: boolean;
  kakaoShareTemplateId?: string;
  useAcqMsg?: boolean;
  accountExpose?: boolean;
  bugos: Bugos;
  createdAt: Date;
  updatedAt: Date;
};

export type FeventOne = Fevent<
  User,
  FuneralHomeInfo,
  undefined,
  ShopOrder<string, string, string, string, string, ShopItem<string, undefined, Image>>[],
  Member<string, Bugo, CashAccount>[],
  Image,
  CashAccount[]
>;

export type FeventList = Fevent<User, FuneralHomeInfo, undefined, undefined, Member[]>[];

export type FeventDoingList = Fevent<
  User,
  FuneralHomeInfo,
  undefined,
  undefined,
  Member[]
>[];

export type FeventByBugoBrand = Fevent<
  User,
  FuneralHomeInfo,
  undefined,
  ShopOrder[],
  string[],
  string,
  undefined,
  string,
  string,
  Pick<Bugo, '_id' | 'lastSentPhoneNumber'>[]
>;

export type FeventByShop = Fevent<
  string,
  FuneralHomeInfo,
  undefined,
  undefined,
  Member<string, Bugo>[],
  string,
  undefined,
  string,
  string,
  undefined,
  BugoBrandBrief
>;

//* 전화주문 화면(부고 조회)용 — 상조명 표기를 위해 bugoBrand만 축약 populate한 fevent
export type FeventWithAgencyBrand = Fevent<
  string,
  FuneralHomeInfo,
  undefined,
  undefined,
  string[],
  string,
  undefined,
  string,
  string,
  undefined,
  BugoBrandBrief
>;

export type FeventState = QueryDataState<FeventOne>;

export type FeventListState = QueryDataState<FeventList>;

export type FeventCountByBugoBrandState = QueryDataState<number>;

export type FeventDoingListState = QueryDataState<FeventDoingList>;

export type FeventListShopOrderItem = {
  _id: string;
  priceRetail: number;
  priceWhole: number;
  priceRebateAgency: number;
  priceRebateWorker: number;
};

export type FeventListBugoItem = {
  _id: string;
  lastSentPhoneNumber?: string;
};

export type FeventListItem = {
  _id: string;
  createdAt: Date;
  workerName: string;
  workerId: string;
  workerTeamName: string;
  workerTeamType: string;
  workerRegion: string;
  registerNumber: string;
  funeralHomeName: string;
  funeralHomeAddress: string;
  deceasedName: string;
  coffinOutDate: Date | null;
  coffinOutTimeDefined: boolean;
  shopOrders: FeventListShopOrderItem[];
  bugos: FeventListBugoItem[];
  memberOrderCount: number;
  shareCount: number;
  isDeleted: boolean;
};

export type FeventListSummary = {
  totalFeventCount: number;
  totalShopOrderCount: number;
  sharedFeventCount: number;
  unsharedFeventCount: number;
  sentBugoCount: number;
  unsentBugoCount: number;
};

export type FeventListResponse = {
  summary?: FeventListSummary;
  data: FeventListItem[];
  pagination?: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
};

export type FeventListAgencyAdminState = QueryDataState<FeventListResponse>;

export type FeventCreate = Omit<
  Fevent,
  | '_id'
  | 'createdAt'
  | 'updatedAt'
  | 'meta'
  | 'courtesyAsset'
  | 'courtesyContent'
  | 'bugos'
>;

export type FeventUpdate<
  User = string,
  FuneralHomeInfo = string,
  Members = undefined,
  ShopOrders = undefined,
  MemberOrders = string[],
  DpImage = string,
  CourtesyAsset = string,
  CourtesyContent = string,
> = {
  _id: string;
  registerNumber?: string;
  bugoBrandAgencyIndex?: number;
  user?: User;
  funeralHomeInfo?: FuneralHomeInfo;
  funeralHomeInfoEmbed?: FuneralHomeInfoEmbed;
  roomInfoEmbed?: {
    name: string;
  };
  members?: Members;
  shopOrders?: ShopOrders;
  memberOrderList?: MemberOrders;
  mournerText?: string;
  deceasedInfo?: {
    age?: number;
    name?: string;
    nameDetail?: string;
    shareDisplayName?: string;
    cemetery?: string;
    coffinIn?: FeventDate;
    coffinOut?: FeventDate;
    death?: FeventDate;
    enterance?: FeventDate;
    note?: string;
    religion?: DeceasedReligion;
    bugoTerm?: string;
    sex?: Gender;
    dpImage?: DpImage;
    //* ⚠️ deceasedInfo 통째 PATCH 라 생략하면 기존 추가 일정이 전부 지워짐 — 항상 배열(빈 [] 포함)로 보낼 것
    extraSchedules?: FeventExtraSchedule[];
    cemeteries?: FeventCemetery[];
  };
  courtesyAsset?: CourtesyAsset;
  courtesyContent?: CourtesyContent;
  status?: FeventStatus;
  hardCustomConfig?: {
    ogImage?: string | null;
    useCustomConfig?: boolean;
    headerImage?: string;
    footerImage?: string;
    hideFlowerOrderLink?: boolean;
    emptyAccountMessage?: string;
    //* 조의금 사절: 부의금 섹션을 안내 문구로 대체(+SMS 기본 문구의 계좌 생략). 구서버 strict 스키마는 조용히 버림 — docs/fevent-hide-condolence-money-spec.md
    hideCondolenceMoney?: boolean;
    condolenceMoneyMessage?: string;
  };
  hiddenBugoShopOrder?: boolean;
  kakaoShareTemplateId?: string;
  useAcqMsg?: boolean;
  accountExpose?: boolean;
};
